import type { Variables } from "@/lib/sparxs/metadataSchemas";
import {
  parseDirectiveRaw,
  type ParsedDirectiveRaw,
} from "../../directives/core/parseRaw";
import {
  getDirectiveConfig,
  type DirectiveChoiceSlot,
  type DirectiveConfigItem,
} from "../../directives/core/config";
import type { DirectiveChipStrategy } from "../../directives/core/chipStrategy";
import { filterVariablesByDirective } from "../variableTokenNodeViewHelpers";

export const normalizeRawFromNodeAttrs = (
  rawAttr: string | undefined,
): string => {
  let r = rawAttr as string;
  if (/^\{\{.*\}\}$/.test(r)) r = r.slice(2, -2);
  return r;
};

export type DirectiveChipDerived = {
  parsed: ParsedDirectiveRaw | null;
  config: DirectiveConfigItem | null;
  filteredVariables: Variables[];
  needsVariable: boolean;
  needsNextChoice: boolean;
  nextChoiceSlot: DirectiveChoiceSlot | undefined;
  isIncomplete: boolean;
};

export const computeDirectiveChipDerived = (
  raw: string,
  allVariables: Variables[],
  strategy: DirectiveChipStrategy | null,
): DirectiveChipDerived => {
  const parsed = parseDirectiveRaw(raw);
  const config =
    parsed != null ? (getDirectiveConfig(parsed.directive) ?? null) : null;
  const filteredVariables =
    config != null
      ? filterVariablesByDirective(allVariables, config.variableTypes)
      : allVariables;

  const needsVariable = (parsed?.variable ?? "") === "";
  const choiceSlotsCount =
    strategy != null && parsed != null && config != null
      ? strategy.getChoiceSlotsCount(parsed, config, allVariables)
      : (config?.choiceSlots.length ?? 0);
  const currentChoices = parsed?.choices ?? [];
  const needsNextChoice =
    (parsed?.variable ?? "") !== "" && currentChoices.length < choiceSlotsCount;
  const nextChoiceIndex = currentChoices.length;
  const nextChoiceSlot =
    strategy != null && parsed != null && config != null
      ? strategy.getNextChoiceSlot(
          parsed,
          config,
          allVariables,
          nextChoiceIndex,
          currentChoices,
        )
      : config?.choiceSlots[nextChoiceIndex];
  const isIncomplete = needsVariable || needsNextChoice;

  return {
    parsed,
    config,
    filteredVariables,
    needsVariable,
    needsNextChoice,
    nextChoiceSlot,
    isIncomplete,
  };
};
